import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";
import type { CaseRecord, Direction } from "../types";
import RecordingDisclaimer from "./RecordingDisclaimer";
import { formatDateTime } from "../lib/formatDate";

interface Props {
  caseId: string;
  onRecordCreated: (record: CaseRecord) => void;
}

type Phase = "idle" | "recording" | "processing" | "error";

// Disclaimer acceptance is remembered for 30 days in localStorage. Within
// that window we skip the modal and go straight to recording; outside, the
// user must re-acknowledge.
const DISCLAIMER_STORAGE_KEY = "casethread:recordingDisclaimerAccepted";
const DISCLAIMER_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function needsDisclaimer(): boolean {
  try {
    const stored = window.localStorage.getItem(DISCLAIMER_STORAGE_KEY);
    if (!stored) return true;
    const accepted = Date.parse(stored);
    if (Number.isNaN(accepted)) return true;
    return Date.now() - accepted > DISCLAIMER_TTL_MS;
  } catch {
    // Private mode / blocked storage — be safe and always show.
    return true;
  }
}

function markDisclaimerAccepted(): void {
  try {
    window.localStorage.setItem(
      DISCLAIMER_STORAGE_KEY,
      new Date().toISOString(),
    );
  } catch {
    // Ignore storage errors; the user will just see the modal again next time.
  }
}

// Hard cap to avoid runaway recordings. Whisper's upload limit is 25MB,
// which at MediaRecorder webm/opus bitrates may be reached before 60
// minutes (Chrome defaults to 128 kbps ≈ ~58 MB/hr). Long calls may be
// rejected at upload time with 413 file_too_large — the user sees the
// size readout climbing past the cap and can stop early.
const MAX_DURATION_SECONDS = 60 * 60;

function pickMimeType(): string | undefined {
  // Browser-specific: Chrome/Firefox prefer webm/opus; Safari needs mp4.
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const c of candidates) {
    if (
      typeof MediaRecorder !== "undefined" &&
      typeof MediaRecorder.isTypeSupported === "function" &&
      MediaRecorder.isTypeSupported(c)
    ) {
      return c;
    }
  }
  return undefined;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function extFor(mime: string): string {
  if (mime.includes("webm")) return "webm";
  if (mime.includes("mp4") || mime.includes("m4a")) return "m4a";
  if (mime.includes("ogg")) return "ogg";
  return "audio";
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function resolveRecordDate(isoFromOcr: string | null): string {
  if (isoFromOcr && /^\d{4}-\d{2}-\d{2}/.test(isoFromOcr)) {
    const d = new Date(isoFromOcr.slice(0, 10) + "T00:00:00Z");
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return new Date().toISOString();
}

export default function RecordCall({ caseId, onRecordCreated }: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<"prompt" | "review" | null>(null);
  // Live recorded-size readout. Updated from ondataavailable so the
  // user can watch the file grow during long calls — if it stops
  // growing, the mic was muted or the track ended and the rest of
  // the recording will be silent.
  const [bytesRecorded, setBytesRecorded] = useState(0);
  const [trackStalled, setTrackStalled] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const bytesRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);
  const startTimeRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);

  const cleanupStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  useEffect(() => () => cleanupStream(), [cleanupStream]);

  const uploadAndSave = useCallback(
    async (file: File) => {
      try {
        const upload = await api.uploadFile(caseId, file);
        const transcript = upload.extracted.body_text ?? "";
        const recordDate = resolveRecordDate(upload.extracted.record_date);
        const subject = `Call — ${formatDateTime(recordDate)}`;
        const direction: Direction =
          upload.extracted.direction === "outbound" ? "outbound" : "inbound";

        const { record } = await api.createRecord(caseId, {
          record_date: recordDate,
          direction,
          source_type: "phone_call",
          from_addr: upload.extracted.from_addr,
          to_addr: upload.extracted.to_addr,
          subject,
          body_text: transcript || null,
          file_r2_key: upload.file_r2_key,
          file_name: upload.file_name,
          file_mime_type: upload.file_mime_type,
        });

        // A true content duplicate is dropped server-side (no record returned).
        if (record) onRecordCreated(record);
        setPhase("idle");
        setElapsed(0);
      } catch (err) {
        setError((err as Error).message);
        setPhase("error");
      }
    },
    [caseId, onRecordCreated],
  );

  function handleRecordClick() {
    if (needsDisclaimer()) {
      setModal("prompt");
      return;
    }
    void startRecording();
  }

  function acceptAndStart() {
    markDisclaimerAccepted();
    setModal(null);
    void startRecording();
  }

  async function startRecording() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Surface mic dropouts. If another app grabs exclusive access to
      // the microphone mid-call (Continuity phone call, FaceTime, Zoom),
      // or the user revokes permission, the track fires `mute` or `ended`
      // but MediaRecorder keeps producing silent encoded frames. Without
      // these listeners the recording silently degrades — the file
      // continues growing but the audio is empty.
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.addEventListener("mute", () => {
          console.warn("[record] audio track muted mid-recording");
          setTrackStalled(true);
        });
        audioTrack.addEventListener("unmute", () => {
          setTrackStalled(false);
        });
        audioTrack.addEventListener("ended", () => {
          console.warn("[record] audio track ended mid-recording");
          setTrackStalled(true);
        });
      }

      const mimeType = pickMimeType();
      // Pin a low bitrate (32 kbps mono opus is plenty for speech) so a
      // full-hour recording fits under the worker's 25 MB upload cap.
      // Chrome's default is ~128 kbps, which would produce ~58 MB/hr and
      // get rejected at upload time.
      const mrOptions: MediaRecorderOptions = { audioBitsPerSecond: 32000 };
      if (mimeType) mrOptions.mimeType = mimeType;
      const mr = new MediaRecorder(stream, mrOptions);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      bytesRef.current = 0;
      setBytesRecorded(0);
      setTrackStalled(false);

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
          bytesRef.current += e.data.size;
          setBytesRecorded(bytesRef.current);
        }
      };
      mr.onstop = async () => {
        const actualType =
          mr.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: actualType });
        cleanupStream();
        if (blob.size === 0) {
          setPhase("error");
          setError("Recording was empty. Try again.");
          return;
        }
        const ext = extFor(actualType);
        const stamp = new Date().toISOString().replace(/[:.]/g, "-");
        const file = new File([blob], `call-${stamp}.${ext}`, {
          type: actualType,
        });
        await uploadAndSave(file);
      };

      mr.start(1000); // emit chunks every second so memory stays bounded
      startTimeRef.current = Date.now();
      setPhase("recording");
      setElapsed(0);
      tickRef.current = window.setInterval(() => {
        const sec = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsed(sec);
        if (sec >= MAX_DURATION_SECONDS) {
          stopRecording();
        }
      }, 500);
    } catch (err) {
      setError(`Microphone unavailable: ${(err as Error).message}`);
      setPhase("error");
      cleanupStream();
    }
  }

  function stopRecording() {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      setPhase("processing");
      mr.stop();
    }
  }

  return (
    <div className="record-call">
      {(phase === "idle" || phase === "error") && (
        <>
          <button
            type="button"
            className="record-call-start"
            onClick={handleRecordClick}
          >
            <span className="record-call-icon" aria-hidden>
              🎙
            </span>
            Record call
          </button>
          <button
            type="button"
            className="record-call-learn-more"
            onClick={() => setModal("review")}
          >
            Recording laws vary by location — learn more
          </button>
          {phase === "error" && error && (
            <div className="record-call-error form-error">⚠ {error}</div>
          )}
        </>
      )}

      {phase === "recording" && (
        <>
          <span className="record-indicator">
            <span className="record-dot" />
            Recording · {formatDuration(elapsed)} · {formatBytes(bytesRecorded)}
          </span>
          <button
            type="button"
            className="btn-danger record-call-stop"
            onClick={stopRecording}
          >
            ■ Stop
          </button>
          {trackStalled && (
            <span className="record-call-warning form-error">
              ⚠ Microphone stopped delivering audio. Another app may be using
              the mic. Stop and re-record.
            </span>
          )}
          <span className="record-call-hint">
            Auto-stops at {formatDuration(MAX_DURATION_SECONDS)} · 25 MB upload
            cap
          </span>
        </>
      )}

      {phase === "processing" && (
        <span className="record-status">
          <span className="spinner" />
          Transcribing…
        </span>
      )}

      <RecordingDisclaimer
        open={modal !== null}
        onCancel={() => setModal(null)}
        onStart={modal === "prompt" ? acceptAndStart : undefined}
      />
    </div>
  );
}
